import * as Schema from "effect/Schema";

import {
  ConversationId,
  IsoDateTime,
  NonNegativeInt,
  OperatorProjectId,
  PositiveInt,
  TrimmedNonEmptyString,
} from "./baseSchemas.ts";

export const SlackId = TrimmedNonEmptyString.check(Schema.isMaxLength(128));
const SlackName = TrimmedNonEmptyString.check(Schema.isMaxLength(80));
export const SlackConversationKind = Schema.Literals(["channel", "dm", "group_dm"]);
export type SlackConversationKind = typeof SlackConversationKind.Type;

export const SlackMemberLink = Schema.Struct({
  principalId: TrimmedNonEmptyString.check(Schema.isMaxLength(256)),
  slackUserId: SlackId,
});
export type SlackMemberLink = typeof SlackMemberLink.Type;

export const SlackConnectionStatus = Schema.Literals([
  "disconnected",
  "connecting",
  "connected",
  "reconnecting",
  "needs_attention",
]);
export type SlackConnectionStatus = typeof SlackConnectionStatus.Type;

export const SlackInstallation = Schema.Struct({
  id: TrimmedNonEmptyString,
  projectId: OperatorProjectId,
  teamId: SlackId,
  teamName: Schema.optionalKey(SlackName),
  appId: SlackId,
  botUserId: SlackId,
  botName: Schema.optionalKey(SlackName),
  botIconUrl: Schema.optionalKey(Schema.String),
  memberLinks: Schema.Array(SlackMemberLink),
  enabled: Schema.Boolean,
  generation: PositiveInt,
  status: SlackConnectionStatus,
  statusMessage: Schema.optionalKey(Schema.String),
  lastConnectedAt: Schema.optionalKey(IsoDateTime),
  testedAt: Schema.optionalKey(IsoDateTime),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type SlackInstallation = typeof SlackInstallation.Type;

export const SlackSetupStatus = Schema.Struct({
  projectId: OperatorProjectId,
  installation: Schema.optionalKey(SlackInstallation),
  manifestJson: Schema.String,
  hostLabel: TrimmedNonEmptyString,
  runtimeAvailable: Schema.Boolean,
});
export type SlackSetupStatus = typeof SlackSetupStatus.Type;

const SlackProjectScope = { projectId: OperatorProjectId };
const SlackSecret = TrimmedNonEmptyString.check(Schema.isMaxLength(4096));
export const SlackActionInput = Schema.Union([
  Schema.Struct({
    action: Schema.Literal("save"),
    ...SlackProjectScope,
    name: SlackName.check(Schema.isMaxLength(35)),
    appToken: SlackSecret,
    botToken: SlackSecret,
  }),
  Schema.Struct({
    action: Schema.Literals(["getAccountLink", "linkAccount"]),
    ...SlackProjectScope,
    token: TrimmedNonEmptyString.check(Schema.isMaxLength(128)),
  }),
  Schema.Struct({ action: Schema.Literal("refreshIdentity"), ...SlackProjectScope }),
  Schema.Struct({ action: Schema.Literal("disconnect"), ...SlackProjectScope }),
]);
export type SlackActionInput = typeof SlackActionInput.Type;

export const SlackActionResult = Schema.Struct({
  status: SlackSetupStatus,
  accountLink: Schema.optionalKey(
    Schema.Struct({ slackUserId: SlackId, slackUserName: Schema.String }),
  ),
  slackUrl: Schema.optionalKey(Schema.String),
  message: Schema.optionalKey(Schema.String),
});
export type SlackActionResult = typeof SlackActionResult.Type;

/** The binding variant used by Slack-created shared conversations. */
export const SlackConversationChannelBinding = Schema.Struct({
  connectorId: Schema.Literal("slack"),
  installationId: SlackId,
  channelId: SlackId,
  externalThreadId: TrimmedNonEmptyString,
  externalParticipantId: Schema.optionalKey(SlackId),
  conversationKind: SlackConversationKind,
});
export type SlackConversationChannelBinding = typeof SlackConversationChannelBinding.Type;

export const SlackInboxState = Schema.Literals([
  "accepted",
  "queued",
  "dispatched",
  "completed",
  "rejected",
]);
export type SlackInboxState = typeof SlackInboxState.Type;

export const SlackDeliveryStatus = Schema.Literals(["pending", "sent", "failed", "uncertain"]);
export type SlackDeliveryStatus = typeof SlackDeliveryStatus.Type;

export const SlackDeliveryKind = Schema.Literals([
  "accepted",
  "queued",
  "approval",
  "rejected",
  "result",
]);
export type SlackDeliveryKind = typeof SlackDeliveryKind.Type;

export const SlackInboxRecord = Schema.Struct({
  generation: PositiveInt,
  id: NonNegativeInt,
  installationId: SlackId,
  eventId: TrimmedNonEmptyString,
  canonicalMessageId: TrimmedNonEmptyString,
  channelId: SlackId,
  conversationKind: SlackConversationKind,
  rootThreadTs: TrimmedNonEmptyString,
  userId: SlackId,
  text: TrimmedNonEmptyString,
  receivedAt: IsoDateTime,
  commandId: TrimmedNonEmptyString,
  state: SlackInboxState,
  conversationId: Schema.optionalKey(ConversationId),
  runId: Schema.optionalKey(TrimmedNonEmptyString),
});
export type SlackInboxRecord = typeof SlackInboxRecord.Type;

export const SlackDeliveryRecord = Schema.Struct({
  id: NonNegativeInt,
  installationId: SlackId,
  sourceKey: TrimmedNonEmptyString,
  generation: PositiveInt,
  runId: TrimmedNonEmptyString,
  conversationId: ConversationId,
  channelId: SlackId,
  threadTs: TrimmedNonEmptyString,
  conversationKind: SlackConversationKind,
  threaded: Schema.Boolean,
  approvalId: Schema.optionalKey(TrimmedNonEmptyString),
  recipientSlackUserId: Schema.optionalKey(SlackId),
  kind: SlackDeliveryKind,
  status: SlackDeliveryStatus,
  externalMessageId: Schema.optionalKey(TrimmedNonEmptyString),
  errorMessage: Schema.optionalKey(Schema.String),
  attempts: NonNegativeInt,
  nextChunk: NonNegativeInt,
  body: Schema.optionalKey(Schema.String),
  nextAttemptAt: Schema.optionalKey(IsoDateTime),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type SlackDeliveryRecord = typeof SlackDeliveryRecord.Type;

export const SlackInteractionState = Schema.Literals(["accepted", "completed", "rejected"]);
export type SlackInteractionState = typeof SlackInteractionState.Type;
export const SlackInteractionRecord = Schema.Struct({
  id: NonNegativeInt,
  installationId: SlackId,
  generation: PositiveInt,
  actionId: TrimmedNonEmptyString,
  approvalId: TrimmedNonEmptyString,
  decision: Schema.Literals(["approve", "reject"]),
  userId: SlackId,
  channelId: SlackId,
  messageTs: TrimmedNonEmptyString,
  commandId: TrimmedNonEmptyString,
  state: SlackInteractionState,
  receivedAt: IsoDateTime,
});
export type SlackInteractionRecord = typeof SlackInteractionRecord.Type;

export const slackManifest = (name: string) => ({
  _metadata: { major_version: 1 },
  display_information: { name, description: "Your team's AI agent" },
  features: {
    app_home: {
      home_tab_enabled: true,
      messages_tab_enabled: true,
      messages_tab_read_only_enabled: false,
    },
    bot_user: { display_name: name, always_online: false },
  },
  oauth_config: {
    scopes: {
      bot: [
        "app_mentions:read",
        "channels:history",
        "groups:history",
        "groups:read",
        "chat:write",
        "channels:read",
        "im:history",
        "im:write",
        "users:read",
        "mpim:read",
        "mpim:history",
        "mpim:write",
        "files:read",
        "files:write",
      ],
    },
  },
  settings: {
    event_subscriptions: {
      bot_events: [
        "app_home_opened",
        "app_uninstalled",
        "tokens_revoked",
        "app_mention",
        "message.channels",
        "message.groups",
        "message.im",
        "message.mpim",
        "agent_session_stopped",
      ],
    },
    interactivity: { is_enabled: true },
    socket_mode_enabled: true,
    org_deploy_enabled: false,
    is_hosted: false,
    token_rotation_enabled: false,
  },
});

export const slackManifestJson = (name: string) => JSON.stringify(slackManifest(name), null, 2);
